import React, { useMemo, useCallback } from 'react'
import { Box, Table, TableHead, TableBody, TableRow, TableCell, Typography, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ScrollTable from 'components/global/CUK/ScrollTable'
import useSort from '../../hooks/use-sort'
import TableSortLabel from 'components/global/TableSort'
import EmptyScreen from 'packages/ui/EmptyScreen'
import AntSwitch from 'packages/ui/Switch'
import { formatBytesWithUnit } from '../../../helpers'

interface TableSectionProps {
  viewType: 'type' | 'ssid' | 'site' | 'evolution'
  verbatim: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectionApi: any
}

const useTable = ({ viewType, verbatim, selectionApi }) => {
  const sortApi = useSort()
  const isChecked = useCallback((item) => {
    if (!selectionApi?.checked?.length) return false
    const normalizedItem = { ...item, name: item.name || '-' }

    if (viewType === 'site') {
      return selectionApi.checked.some(checkedItem =>
        String(checkedItem.name || '-') === String(normalizedItem.name) && checkedItem.site_id === normalizedItem.site_id
      )
    }

    return selectionApi.checked.some(checkedItem =>
      String(checkedItem.name || '-') === String(normalizedItem.name) && checkedItem.network_name === normalizedItem.network_name
    )
  }, [selectionApi?.checked, viewType])

  const getColor = useCallback((item) => {
    if (!selectionApi?.checked?.length) return '#cccccc'
    const normalizedItem = { ...item, name: item.name || '-' }

    let checkedItem
    if (viewType === 'site') {
      checkedItem = selectionApi.checked.find(checkedItem =>
        String(checkedItem.name || '-') === String(normalizedItem.name) && checkedItem.site_id === normalizedItem.site_id
      )
    } else {
      checkedItem = selectionApi.checked.find(checkedItem =>
        String(checkedItem.name || '-') === String(normalizedItem.name) && checkedItem.network_name === normalizedItem.network_name
      )
    }

    return checkedItem?.color || '#cccccc'
  }, [selectionApi?.checked, viewType])

  const isHovered = useCallback((item) => {
    if (!selectionApi?.hoveredItem) return false
    const normalizedItem = { ...item, name: item.name || '-' }

    if (viewType === 'site') {
      return String(selectionApi.hoveredItem.name || '-') === String(normalizedItem.name) &&
        selectionApi.hoveredItem.site_id === normalizedItem.site_id
    }

    return String(selectionApi.hoveredItem.name || '-') === String(normalizedItem.name) &&
      selectionApi.hoveredItem.network_name === normalizedItem.network_name
  }, [selectionApi?.hoveredItem, viewType])

  const dataSource = useMemo(() => {
    if (viewType === 'site') {
      return selectionApi?.sites || []
    }
    return selectionApi?.ssids || []
  }, [viewType, selectionApi])

  const filteredData = useMemo(() => {
    if (!dataSource?.length) return []

    return dataSource.filter(({ name = '-', total = 0 }) => {
      const searchName = String(name || '-')
      const nameMatch = searchName.toLowerCase().includes(verbatim)
      const hasTraffic = total > 0
      return nameMatch && hasTraffic
    }).map(item => ({
      ...item,
      name: item.name || '-'
    }))
  }, [dataSource, verbatim])

  const sortedData = useMemo(() => {
    if (!sortApi?.sort || !filteredData.length) return []
    return sortApi.sort(filteredData)
  }, [sortApi?.sort, filteredData])

  const topTenItems = useMemo(() => {
    return sortedData
      .slice()
      .sort((a, b) => ((b.upload || 0) + (b.download || 0)) - ((a.upload || 0) + (a.download || 0)))
      .slice(0, 10)
  }, [sortedData])

  const selectFirstTen = useMemo(() => {
    if ((viewType === 'ssid' || viewType === 'site') && topTenItems.length > 0) {
      return topTenItems.every(item => isChecked(item))
    }
    return false
  }, [viewType, topTenItems, isChecked])

  const handleSwitchToggle = useCallback((item) => {
    if (selectionApi?.onToggle) {
      selectionApi.onToggle(item)
    }
  }, [selectionApi?.onToggle])

  const handleSelectFirstTenToggle = useCallback(() => {
    if (selectionApi?.onToggleAll) {
      selectionApi.onToggleAll()
    }
  }, [selectionApi?.onToggleAll])

  const handleMouseEnter = useCallback((item) => {
    if (selectionApi?.onMouseHover) {
      selectionApi.onMouseHover(item)
    }
  }, [selectionApi?.onMouseHover])

  const handleMouseLeave = useCallback(() => {
    if (selectionApi?.onMouseOut) {
      selectionApi.onMouseOut()
    }
  }, [selectionApi?.onMouseOut])

  return {
    handleMouseLeave,
    handleMouseEnter,
    handleSelectFirstTenToggle,
    handleSwitchToggle,
    selectFirstTen,
    sortedData,
    dataSource,
    filteredData,
    verbatim,
    isChecked,
    getColor,
    isHovered,
  }
}

const TableSwitch: React.FC<TableSectionProps> = ({ viewType, verbatim, selectionApi }) => {
  const { t, i18n } = useTranslation()
  const sortApi = useSort()
  const tableApi = useTable({viewType, verbatim, selectionApi})

  if (!tableApi.sortedData.length) {
    return (
      <Box sx={{ 
        width: { xs: '100%', md: '40%' }, 
        border: '1px dashed #D7DAEA', 
        borderRadius: '16px', 
        p: 2,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <EmptyScreen 
          icon="square-poll-vertical" 
          variant="compact"
          iconSize="32px" 
          fontSize="14px"
          spacing={1.5}
        >
          {t('stats.traffic.table.no_data')}
        </EmptyScreen>
      </Box>
    )
  }

  const titleKey = viewType === 'site' ? 'stats.traffic.table.site_distribution_title' : 'stats.traffic.table.ssid_distribution_title'

  return (
    <Box sx={{ 
      width: { xs: '100%', md: '40%' }, 
      border: '1px solid #EFEFEF', 
      borderRadius: '16px', 
      p: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography sx={{ 
        fontWeight: 600, 
        fontSize: '12px', 
        mb: 2, 
        textTransform: 'uppercase',
        textAlign: 'center'
      }}>
        {t(titleKey)}
      </Typography>
      
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <ScrollTable height={140} shouldFetchNextPage={false}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '50%', pl: 2 }}>
                  <TableSortLabel
                    orderName="label"
                    orderBy={sortApi?.orderBy}
                    onOrderByChanged={sortApi?.orderByChange}
                    direction={sortApi?.orderDirection}
                  >
                    {t('stats.traffic.table.headers.label_type')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '15%', pl: 2 }}>
                  <AntSwitch
                    checked={tableApi.selectFirstTen}
                    onChange={tableApi.handleSelectFirstTenToggle}
                    size="small"
                    disabled={!tableApi.sortedData.length}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#1976d2',
                        opacity: 1,
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: '17.5%', pl: 2 }}>
                  <TableSortLabel
                    orderName="traffic"
                    orderBy={sortApi?.orderBy}
                    onOrderByChanged={sortApi?.orderByChange}
                    direction={sortApi?.orderDirection}
                  >
                    {t('stats.traffic.table.headers.download')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '17.5%', pl: 2 }}>{t('stats.traffic.table.headers.ratio')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableApi.sortedData.map((item, index) => {
                const checked = tableApi.isChecked(item)
                const color = tableApi.getColor(item)
                const itemHovered = tableApi.isHovered(item)
                const { value, unit } = formatBytesWithUnit(item.total, i18n.language)
                
                return (
                  <TableRow 
                    key={`${item.name || 'no-name'}-${item.site_id || item.network_name || index}`}
                    onMouseEnter={() => tableApi.handleMouseEnter(item)}
                    onMouseLeave={() => tableApi.handleMouseLeave()}
                    sx={{
                      backgroundColor: itemHovered ? '#F5F5F5' : 'transparent',
                      '&:hover': {
                        backgroundColor: '#F5F5F5'
                      }
                    }}
                  >
                    <TableCell sx={{ width: '50%', pl: 2 }}>
                      <Stack>
                        <Typography sx={{ fontWeight: 400, fontSize: '12px'}}>
                          {item.name}
                        </Typography>
                        {viewType === 'ssid' && (
                          <Typography sx={{ fontWeight: 400, fontSize: '10px', color: '#989898'}}>
                            {t(`stats.legends.types.${item.network_name}`)}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ width: '15%', pl: 2 }}>
                      <AntSwitch
                        checked={checked}
                        onChange={() => tableApi.handleSwitchToggle(item)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: color,
                            opacity: 1,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: '17.5%', pl: 2 }}>
                      <Typography sx={{ fontSize: '12px'}}>
                        {value}
                        <Typography component="span" sx={{ fontSize: '10px', color: '#989898', ml: 0.5 }}>
                          {unit}
                        </Typography>
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '17.5%', pl: 2 }}>
                      <Typography sx={{ fontSize: '12px'}}>
                        {item.percentage}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </ScrollTable>
      </Box>
    </Box>
  )
}

export default TableSwitch